import {ReactElement} from 'react';
import {I18n} from 'aws-amplify/utils';
import {Box} from '@mantine/core';
import {
    ReloadButton,
    SimplePageHeader,
} from '@flexinet/ui-components';

import {useDnsRecords} from '../hooks/useDnsRecords';
import {useDnsRecordsSelection} from '../hooks/useDnsRecordsSelection';
import {useRecordWriteCapabilities} from '../hooks/useRecordWriteCapabilities';
import {useRecordDeletion} from '../hooks/useRecordDeletion';
import {AddRecordButton} from '../buttons/AddRecordButton';
import {DeleteRecordButton} from '../buttons/DeleteRecordButton';
import {FilterRecordsButton} from '../buttons/FilterRecordsButton';
import {DeleteRecordModal} from './DeleteRecordModal';
import {showErrorNotification} from '../../../components/ServiceNotifications';

const RecordsReloadButton = (
    {
        connectionId,
        zoneId,
    }: {
        connectionId?: string,
        zoneId?: string,
    },
) => {
    const {reload} = useDnsRecords(connectionId, zoneId);
    return (
        <Box mx={'s'}>
            <ReloadButton onClick={() => reload()}/>
        </Box>
    );
};

// The toolbar's delete affordance, acting on the table's selection.
//
// ⚠️ HIDDEN, NOT DISABLED, when the credential cannot write records — except
// at zero selection, which stays visible-but-disabled so the toolbar does not
// jump as rows are ticked.
const DeleteRecordTool = (
    {
        connectionId,
        zoneId,
        zoneName,
    }: {
        connectionId?: string,
        zoneId?: string,
        zoneName?: string,
    },
) => {

    const {selectedRecords} = useDnsRecordsSelection();
    const {canWriteRecords} = useRecordWriteCapabilities(connectionId);
    const deletion = useRecordDeletion(connectionId, zoneId);

    const nothingSelected = selectedRecords.length === 0;

    if (!nothingSelected && !canWriteRecords) {
        return null;
    }

    const handleDelete = () => {
        // ⚠️ REFUSES A MULTI-ROW DELETE rather than fanning out. Each record
        // carries its own consequence — deleting an NS record breaks a
        // delegation, deleting an A record takes a host offline — and a batch
        // reports one outcome for several. The confirmation names ONE record,
        // and it can only do that honestly for one.
        if (selectedRecords.length > 1) {
            showErrorNotification(
                I18n.get('Delete one record at a time. Each record has its own consequence — a nameserver record is a delegation — and a batch would report one outcome for all of them.'),
            );
            return;
        }
        if (selectedRecords[0]) {
            deletion.requestDelete(selectedRecords[0]);
        }
    };

    return (
        <>
            <DeleteRecordButton
                count={selectedRecords.length}
                onClick={handleDelete}
            />
            <DeleteRecordModal
                record={deletion.record}
                zoneName={zoneName}
                opened={deletion.opened}
                onClose={deletion.cancelDelete}
                onConfirm={deletion.confirmDelete}
                isDeleting={deletion.isDeleting}
                error={deletion.error}
            />
        </>
    );
};

export const IndexPageHeader = (
    {
        connectionId,
        zoneId,
        zoneName,
    }: {
        connectionId?: string,
        zoneId?: string,
        zoneName?: string,
    },
) => {

    // Add and Delete hide themselves on capability rather than being
    // conditionally rendered here — the decision belongs with the control that
    // knows which connection it is asking about.
    const tools: ReactElement[] = [
        <AddRecordButton key={'add'} connectionId={connectionId} zoneId={zoneId}/>,
        <DeleteRecordTool
            key={'delete'}
            connectionId={connectionId}
            zoneId={zoneId}
            zoneName={zoneName}
        />,
        <FilterRecordsButton key={'filter'} connectionId={connectionId}/>,
        <RecordsReloadButton key={'reload'} connectionId={connectionId} zoneId={zoneId}/>,
    ];

    return (
        <SimplePageHeader
            // ⚠️ NAMES THE ZONE where it is known. "Records" alone leaves an
            // operator with several zones open unable to tell which one they
            // are editing, which is the misread that makes a delete dangerous.
            title={zoneName
                ? `${I18n.get('Records')} — ${zoneName}`
                : I18n.get('Records')}
            tools={tools}
        />
    );
};
